import type { TProjectVersion } from '@newo/api/gateway';
import { ProjectVersionStatus } from '@newo/api/gateway';
import type { TOpenFile } from './DesignerFiles.store';
import { FileSaveConflictError } from './Files.queries';

type SaveFile = (file: {
    path: string;
    content: string;
    baseGeneration: string | null;
}) => Promise<unknown>;

type ResolveSaveConflict = (
    error: FileSaveConflictError,
    file: TOpenFile
) => Promise<boolean>;

export const autoSelectVersion = (
    versions: TProjectVersion[]
): TProjectVersion | undefined => {
    if (versions.length === 0) {
        return undefined;
    }
    const sorted = Array.from(versions).sort(
        (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    const nonPublished = sorted.find(
        (version) => version.status !== ProjectVersionStatus.Published
    );
    if (nonPublished) {
        return nonPublished;
    }
    return sorted.at(0);
};

export const getDirtyFilePaths = (
    openFiles: Partial<Record<string, TOpenFile>>
): string[] =>
    Object.keys(openFiles).filter((path) => openFiles[path]?.dirty === true);

export const saveDirtyFiles = async (
    dirtyFilePaths: string[],
    openFiles: Partial<Record<string, TOpenFile>>,
    saveFile: SaveFile,
    resolveSaveConflict?: ResolveSaveConflict
): Promise<boolean> => {
    for (const path of dirtyFilePaths) {
        const file = openFiles[path];
        if (!file?.dirty) {
            continue;
        }

        try {
            await saveFile({
                path,
                content: file.content,
                baseGeneration: file.baseGeneration
            });
        } catch (error) {
            if (error instanceof FileSaveConflictError && resolveSaveConflict) {
                const shouldContinue = await resolveSaveConflict(error, file);
                if (shouldContinue) {
                    continue;
                }
            }
            return false;
        }
    }
    return true;
};
